import {FC, useMemo} from "react";
import {CreateResultNode, CreateResultSummary} from "../../view-model";
import {summarizeResult} from "../../helpers";
import {usePlan} from "../../context";
import {validateAndExtractTenantIdForHierarchy} from "../../transforms";
import {CreateResultTreeView, SummaryCard} from "../common";
import {ResultsStepNodeLabel} from "./ResultsStepNodeLabel";
import "../common/BuilderStep.scss";

export interface ResultsStepProps {
  result: CreateResultNode;
}

export const ResultsStep: FC<ResultsStepProps> = ({result}) => {
  const {hierarchy} = usePlan();

  const summary = useMemo<CreateResultSummary>(
    () => summarizeResult(result),
    [result]
  );

  const hasExistingTenant = useMemo(() => {
    try {
      return !!validateAndExtractTenantIdForHierarchy(hierarchy);
    } catch {
      return false;
    }
  }, [hierarchy]);

  const tenantBaseLabelKey =
    result.status === "in-progress" || result.status === "queued"
      ? "create-operational-hierarchy.plan-summary"
      : "create-operational-hierarchy.result-summary";

  const tenantQualifier = (() => {
    if (result.status === "in-progress" || result.status === "queued") {
      return hasExistingTenant ? "existing-tenant" : "new-tenant";
    }
    return hasExistingTenant ? "tenant-assigned" : "tenant-created";
  })();

  const tenantCount =
    result.status === "failed"
      ? 0
      : result.status === "in-progress" ||
          result.status === "queued" ||
          result.status === "created" ||
          hasExistingTenant
        ? 1
        : 0;

  const tenantName = hierarchy.custName ?? "";

  return (
    <section
      data-testid={"results-step"}
      className={"hierarchy-builder-step results-step"}>
      <section data-testid={"summary-section"} className={"summary-section"}>
        <SummaryCard
          baseKey={"create-operational-hierarchy.result-summary.created"}
          count={summary.created.length}
        />
        <SummaryCard
          baseKey={"create-operational-hierarchy.result-summary.operational"}
          count={summary.operational.length}
        />
        <SummaryCard
          baseKey={"create-operational-hierarchy.result-summary.failed"}
          count={summary.failed.length}
        />
        <SummaryCard
          baseKey={"create-operational-hierarchy.result-summary.skipped"}
          count={summary.skipped.length}
        />
        <SummaryCard
          baseKey={tenantBaseLabelKey}
          qualifier={tenantQualifier}
          count={tenantCount}
          name={tenantName}
        />
      </section>
      <section
        data-testid={"hierarchy-section"}
        className={"hierarchy-section"}>
        <CreateResultTreeView result={result} label={ResultsStepNodeLabel} />
      </section>
    </section>
  );
};
